// ===============================
// src/ConfigDesiderataInterne2.jsx
// ===============================
// Page Responsable : configuration des desiderata de gardes (Interne Port Royal 2)
// - Lit/écrit via /api/v2/config/desiderata-interne2 (backend)
// - ✅ (AJOUT MINIMAL) support multi-mois via cfg.mois_ouverts
// ===============================

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import LogoutButton from "./components/LogoutButton.jsx";
import { confirmDialog } from "./ui/dialogService.js";
import { apiFetch } from "./apiMedecins.js";
import { goContextualBack } from "./contextualBack.js";

const MOIS_FR = [
  "Janvier",
  "Février",
  "Mars",
  "Avril",
  "Mai",
  "Juin",
  "Juillet",
  "Août",
  "Septembre",
  "Octobre",
  "Novembre",
  "Décembre",
];

function pad2(n) {
  return String(n).padStart(2, "0");
}

function makeMoisCle(annee, moisIndex) {
  const y = Number(annee);
  const mi = Number(moisIndex);
  if (!Number.isFinite(y) || !Number.isFinite(mi)) return "";
  return `${y}-${pad2(mi + 1)}`;
}

function makeLabel(annee, moisIndex) {
  const y = Number(annee);
  const mi = Number(moisIndex);
  if (!Number.isFinite(y) || !Number.isFinite(mi) || mi < 0 || mi > 11) return "";
  return `${MOIS_FR[mi]} ${y}`;
}

// fallback simple "dernier jour du mois précédent" (ex: Mars -> 28/02 ou 29/02)
function lastDayOfPrevMonthIso(annee, moisIndex) {
  const y = Number(annee);
  const mi = Number(moisIndex);
  if (!Number.isFinite(y) || !Number.isFinite(mi)) return "";
  // deadline = dernier jour du mois précédent
  const d = new Date(y, mi, 0); // jour 0 du moisIndex => dernier jour du mois précédent
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function isoToFr(iso) {
  if (!iso || typeof iso !== "string") return "";
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return "";
  return `${m[3]}/${m[2]}/${m[1]}`;
}

export default function ConfigDesiderataInterne2() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);

  // ✅ multi-mois
  const [moisOuverts, setMoisOuverts] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(0);

  const styles = useMemo(
    () => ({
      container: {
        maxWidth: 980,
        margin: "40px auto",
        padding: 24,
        fontFamily: "Inter, sans-serif",
      },
      title: { fontSize: 28, fontWeight: 800, margin: "16px 0 8px" },
      subtitle: { color: "#374151", marginBottom: 16, lineHeight: 1.45 },
      btn: (disabled = false, variant = "dark") => ({
        padding: "10px 18px",
        borderRadius: 10,
        border: "1px solid #111827",
        background:
          variant === "dark"
            ? disabled
              ? "#6b7280"
              : "#111827"
            : "#ffffff",
        color: variant === "dark" ? "#ffffff" : "#111827",
        cursor: disabled ? "not-allowed" : "pointer",
        fontWeight: 700,
        opacity: disabled ? 0.85 : 1,
      }),
      card: {
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        padding: 16,
        background: "#fff",
      },
      grid: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 12,
      },
      field: { display: "flex", flexDirection: "column", gap: 6 },
      label: { fontSize: 13, fontWeight: 700, color: "#111827" },
      input: {
        padding: "10px 12px",
        borderRadius: 10,
        border: "1px solid #d1d5db",
        fontSize: 14,
      },
      hint: { fontSize: 12, color: "#6b7280", marginTop: 6 },
      row: { display: "flex", gap: 12, flexWrap: "wrap", marginTop: 16, alignItems: "center" },
      ok: { color: "#065f46", fontWeight: 800, marginTop: 12 },
      err: { color: "#b91c1c", fontWeight: 800, marginTop: 12 },
      warn: { color: "#92400e", fontWeight: 800, marginTop: 12 },
      select: {
        padding: "10px 12px",
        borderRadius: 10,
        border: "1px solid #d1d5db",
        fontSize: 14,
        minWidth: 320,
      },
      small: { fontSize: 12, color: "#6b7280" },
      badge: (primary) => ({
        display: "inline-flex",
        alignItems: "center",
        padding: "4px 10px",
        borderRadius: 9999,
        border: "1px solid #d1d5db",
        background: primary ? "#eef2ff" : "#f9fafb",
        color: primary ? "#1d4ed8" : "#374151",
        fontSize: 12,
        fontWeight: 800,
      }),
    }),
    []
  );

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        setMsg(null);

        const res = await apiFetch("/api/v2/config/desiderata-interne2");
        if (!res.ok) {
          const t = await res.text().catch(() => "");
          throw new Error(`HTTP ${res.status} lecture config. ${t}`);
        }

        const json = (await res.json()) || {};

        // ✅ multi-mois : utiliser mois_ouverts tel quel (même vide = désidératas fermés)
        // Le fallback legacy n'est utilisé que si mois_ouverts n'existe pas du tout
        let arr = [];
        if (Array.isArray(json.mois_ouverts)) {
          // mois_ouverts existe (même vide) => l'utiliser directement
          arr = json.mois_ouverts;
        } else if (json.mois_cle) {
          // mois_ouverts n'existe pas du tout => fallback legacy (ancienne config)
          arr = [
            {
              annee: json.annee,
              mois_index: json.mois_index,
              mois_label: json.mois_label,
              mois_cle: json.mois_cle,
              deadline_iso: json.deadline_iso,
              deadline_affichage: json.deadline_affichage,
            },
          ];
        }

        setMoisOuverts(arr);
        setSelectedIdx(0);
      } catch (e) {
        console.error(e);
        setError(e.message || "Erreur inconnue");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const current = useMemo(() => {
    return moisOuverts[selectedIdx] || null;
  }, [moisOuverts, selectedIdx]);

  const setField = (k, v) => {
    setMsg(null);
    setError(null);
    setMoisOuverts((prev) => {
      const next = [...prev];
      if (!next[selectedIdx]) return prev;
      next[selectedIdx] = { ...next[selectedIdx], [k]: v };
      return next;
    });
  };

  const autoMoisCle = () => {
    if (!current) return;
    const mc = makeMoisCle(current.annee, current.mois_index);
    setField("mois_cle", mc);
  };

  const autoLabel = () => {
    if (!current) return;
    const ml = makeLabel(current.annee, current.mois_index);
    setField("mois_label", ml);
  };

  const addNextMonth = () => {
    if (!current) return;
    const y = Number(current.annee);
    const mi = Number(current.mois_index);
    if (!Number.isFinite(y) || !Number.isFinite(mi)) return;

    let ny = y;
    let nmi = mi + 1;
    if (nmi > 11) {
      nmi = 0;
      ny = y + 1;
    }

    const entry = {
      annee: ny,
      mois_index: nmi,
      mois_label: makeLabel(ny, nmi),
      mois_cle: makeMoisCle(ny, nmi),
      deadline_iso: lastDayOfPrevMonthIso(ny, nmi),
      deadline_affichage: isoToFr(lastDayOfPrevMonthIso(ny, nmi)),
    };

    setMoisOuverts((prev) => {
      const next = [...prev, entry];
      return next;
    });
    setSelectedIdx(moisOuverts.length); // pointe sur le nouveau (index = ancien length)
    setMsg(null);
    setError(null);
  };

  const setPrimary = () => {
    if (!current) return;
    setMoisOuverts((prev) => {
      const next = [...prev];
      const picked = next.splice(selectedIdx, 1)[0];
      next.unshift(picked);
      return next;
    });
    setSelectedIdx(0);
    setMsg(null);
    setError(null);
  };

  const removeCurrent = async () => {
    if (!current) return;
    const confirmMsg = moisOuverts.length <= 1 
      ? "⚠️ Attention : c'est le dernier mois ouvert. Si vous le supprimez, les désidératas seront FERMÉS pour Interne Port Royal 2 (aucun médecin ne pourra saisir). Continuer ?"
      : "Supprimer ce mois ouvert ?";
    if (!(await confirmDialog(confirmMsg))) return;

    setMoisOuverts((prev) => {
      const next = [...prev];
      next.splice(selectedIdx, 1);
      return next;
    });
    setSelectedIdx((i) => Math.max(0, i - 1));
    setMsg(null);
    setError(null);
  };

  // ✅ Fermer tous les désidératas (vider mois_ouverts ET sauvegarder immédiatement)
  const closeAllDesiderata = async () => {
    if (!(await confirmDialog("⚠️ Fermer TOUS les désidératas Interne Port Royal 2 ?\n\nLes médecins ne pourront plus saisir leurs désidératas tant que vous ne rouvrirez pas un mois."))) return;
    
    try {
      setSaving(true);
      setMsg(null);
      setError(null);

      const res = await apiFetch("/api/v2/config/desiderata-interne2", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mois_ouverts: [] }),
      });

      if (!res.ok) {
        const t = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status} fermeture désidératas. ${t}`);
      }

      setMoisOuverts([]);
      setSelectedIdx(0);
      setMsg("✅ Désidératas Interne Port Royal 2 FERMÉS (aucun mois ouvert).");
    } catch (e) {
      console.error(e);
      setError(e.message || "Erreur lors de la fermeture des désidératas");
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    try {
      setSaving(true);
      setMsg(null);
      setError(null);

      const payload = {
        mois_ouverts: moisOuverts.map((m) => ({
          ...m,
          annee: Number(m.annee),
          mois_index: Number(m.mois_index),
        })),
      };

      const res = await apiFetch("/api/v2/config/desiderata-interne2", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const t = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status} sauvegarde config. ${t}`);
      }

      if (moisOuverts.length === 0) {
        setMsg("✅ Désidératas Interne Port Royal 2 FERMÉS (aucun mois ouvert).");
      } else {
        setMsg(`✅ Configuration Interne Port Royal 2 enregistrée (${moisOuverts.length} mois ouvert${moisOuverts.length > 1 ? "s" : ""}).`);
      }
    } catch (e) {
      console.error(e);
      setError(e.message || "Erreur inconnue");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <button style={styles.btn(false)} onClick={() => goContextualBack(navigate, "/responsable/gardes?type=INTERNE2")}>
          ← Retour
        </button>
        <p style={{ marginTop: 14 }}>Chargement de la configuration Interne Port Royal 2…</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px", flexWrap: "wrap", gap: "12px" }}>
        <LogoutButton />
      </div>

      <h1 style={styles.title}>Configuration des desiderata de garde Interne Port Royal 2</h1>
      <p style={styles.subtitle}>
        ✅ Objectif : ouvrir <b>plusieurs mois</b> (ex: Février + Mars) en parallèle pour Interne Port Royal 2.
        <br />
        <span style={styles.small}>
          <b>mois_index</b> est en base 0 (0=janvier, 1=février, …). Le <b>mois principal</b> est le premier de la liste.
        </span>
      </p>

      <div style={styles.card}>
        {/* ✅ Cas où aucun mois n'est ouvert */}
        {moisOuverts.length === 0 ? (
          <div style={{ padding: "20px 0" }}>
            <div style={{ ...styles.warn, marginTop: 0, marginBottom: 16 }}>
              🔒 Les désidératas Interne Port Royal 2 sont actuellement <b>FERMÉS</b> (aucun mois ouvert).
            </div>
            <p style={{ color: "#374151", marginBottom: 16 }}>
              Les médecins ne peuvent pas saisir leurs désidératas. Pour les rouvrir, ajoutez un mois ci-dessous :
            </p>
            <button
              type="button"
              style={styles.btn(false)}
              onClick={() => {
                const now = new Date();
                const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
                const y = nextMonth.getFullYear();
                const mi = nextMonth.getMonth();
                const entry = {
                  annee: y,
                  mois_index: mi,
                  mois_label: makeLabel(y, mi),
                  mois_cle: makeMoisCle(y, mi),
                  deadline_iso: lastDayOfPrevMonthIso(y, mi),
                  deadline_affichage: isoToFr(lastDayOfPrevMonthIso(y, mi)),
                };
                setMoisOuverts([entry]);
                setSelectedIdx(0);
              }}
            >
              + Ouvrir les désidératas (mois prochain)
            </button>
            {msg ? <div style={styles.ok}>{msg}</div> : null}
            {error ? <div style={styles.err}>{error}</div> : null}
          </div>
        ) : (
          <>
        <div style={styles.row}>
          <select
            style={styles.select}
            value={selectedIdx}
            onChange={(e) => setSelectedIdx(Number(e.target.value))}
          >
            {moisOuverts.map((m, idx) => (
              <option key={String(m.mois_cle || idx)} value={idx}>
                {m.mois_label || m.mois_cle || `Mois #${idx + 1}`} {idx === 0 ? "— (principal)" : ""}
              </option>
            ))}
          </select>

          <span style={styles.badge(selectedIdx === 0)}>Mois {selectedIdx === 0 ? "principal" : "ouvert"}</span>

          <button type="button" style={styles.btn(false, "light")} onClick={addNextMonth}>
            + Ajouter le mois suivant
          </button>

          <button type="button" style={styles.btn(false, "light")} onClick={setPrimary}>
            Définir comme principal
          </button>

          <button type="button" style={styles.btn(false, "light")} onClick={removeCurrent}>
            Supprimer ce mois
          </button>

          <button type="button" style={{ ...styles.btn(false, "light"), color: "#b91c1c", borderColor: "#b91c1c" }} onClick={closeAllDesiderata}>
            🔒 Fermer tous les désidératas
          </button>
        </div>

        {!current ? (
          <div style={styles.err}>Aucun mois sélectionné.</div>
        ) : (
          <>
            <div style={{ ...styles.grid, marginTop: 12 }}>
              <div style={styles.field}>
                <div style={styles.label}>Année</div>
                <input
                  style={styles.input}
                  value={current.annee ?? ""}
                  onChange={(e) => setField("annee", e.target.value)}
                  placeholder="2026"
                />
              </div>

              <div style={styles.field}>
                <div style={styles.label}>mois_index (0=janvier … 11=décembre)</div>
                <input
                  style={styles.input}
                  value={current.mois_index ?? ""}
                  onChange={(e) => setField("mois_index", e.target.value)}
                  placeholder="1"
                />
              </div>

              <div style={styles.field}>
                <div style={styles.label}>Libellé mois (affichage)</div>
                <input
                  style={styles.input}
                  value={current.mois_label ?? ""}
                  onChange={(e) => setField("mois_label", e.target.value)}
                  placeholder="Février 2026"
                />
                <div style={styles.hint}>
                  Astuce : tu peux cliquer "Auto libellé" si tu modifies année/mois_index.
                </div>
              </div>

              <div style={styles.field}>
                <div style={styles.label}>Clé mois (YYYY-MM)</div>
                <input
                  style={styles.input}
                  value={current.mois_cle ?? ""}
                  onChange={(e) => setField("mois_cle", e.target.value)}
                  placeholder="2026-02"
                />
                <div style={styles.hint}>
                  Astuce : si tu changes année/mois_index, clique "Auto mois clé".
                </div>
              </div>

              <div style={styles.field}>
                <div style={styles.label}>Date limite (ISO, YYYY-MM-DD)</div>
                <input
                  style={styles.input}
                  value={current.deadline_iso ?? ""}
                  onChange={(e) => setField("deadline_iso", e.target.value)}
                  placeholder="2026-01-31"
                />
              </div>

              <div style={styles.field}>
                <div style={styles.label}>Date limite (affichage, JJ/MM/AAAA)</div>
                <input
                  style={styles.input}
                  value={current.deadline_affichage ?? ""}
                  onChange={(e) => setField("deadline_affichage", e.target.value)}
                  placeholder="31/01/2026"
                />
              </div>
            </div>

            <div style={styles.row}>
              <button type="button" style={styles.btn(false, "light")} onClick={autoMoisCle}>
                Auto mois clé
              </button>

              <button type="button" style={styles.btn(false, "light")} onClick={autoLabel}>
                Auto libellé
              </button>

              <button type="button" style={styles.btn(saving)} onClick={save} disabled={saving}>
                {saving ? "Enregistrement…" : "Enregistrer"}
              </button>
            </div>

            <div style={styles.hint}>
              👉 Pour "ouvrir mars tout en gardant février" : sélectionne Février, clique "Ajouter le mois suivant"
              (ça crée Mars), ajuste la deadline si besoin, puis "Enregistrer".
            </div>

            {msg ? <div style={styles.ok}>{msg}</div> : null}
            {error ? <div style={styles.err}>{error}</div> : null}
          </>
        )}
          </>
        )}
      </div>
    </div>
  );
}
